import React, { useState } from "react";
import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  useToast,
  Spinner,
  Box,
} from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { DataTable } from "../components/DataTable";
import {
  TableFilterDefinition,
  TableFilterValues,
  TableQueryState,
  TableSortState,
} from "../types";
import { useAuth } from "../context/AuthContext";
import { RecordDetailsModal } from "./RecordDetailsModal";

type CrudColumn = {
  header: string;
  accessor: string;
  isSortable?: boolean;
  render?: (item: any) => React.ReactNode;
  excludeFromExport?: boolean;
  exportValue?: (item: any) => any;
};

interface CrudPageProps {
  title: string;
  columns: CrudColumn[];
  queryKey: string;
  dataKey?: string;
  api: {
    getAll: (
      page: number,
      pageSize: number,
      tableQuery?: TableQueryState,
    ) => Promise<any>;
    create?: (data: any) => Promise<any>;
    update?: (id: string, data: any) => Promise<any>;
    delete?: (id: string) => Promise<any>;
  };
  FormComponent?: React.ComponentType<{
    initialData?: any;
    onSubmit: (data: any) => void;
    onCancel: () => void;
  }>;
  resourceName?: string;
  showAddButton?: boolean;
  enableEdit?: boolean;
  enableDelete?: boolean;
  enableView?: boolean;
  pageSize?: number;
  queryDependencies?: any[];
  queryEnabled?: boolean;
  keepPreviousData?: boolean;
  beforeTable?: React.ReactNode;
  afterTable?: React.ReactNode;
  customAddAction?: () => void;
  customViewAction?: (item: any) => void | Promise<void>;
  customEditAction?: (item: any) => void;
  customDeleteAction?: (item: any) => void;
  errorMessage?: string;
  onDataLoaded?: (data: any) => void;
  filterDefinitions?: TableFilterDefinition[];
  initialSort?: TableSortState;
}

export const CrudPage: React.FC<CrudPageProps> = ({
  title,
  columns,
  queryKey,
  dataKey,
  api,
  FormComponent,
  resourceName,
  showAddButton = true,
  enableEdit = true,
  enableDelete = true,
  enableView = false,
  pageSize = 20,
  queryDependencies = [],
  queryEnabled = true,
  keepPreviousData = true,
  beforeTable,
  afterTable,
  customAddAction,
  customViewAction,
  customEditAction,
  customDeleteAction,
  errorMessage = "دریافت اطلاعات با خطا مواجه شد.",
  onDataLoaded,
  filterDefinitions = [],
  initialSort,
}) => {
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<TableSortState | undefined>(initialSort);
  const [filterValues, setFilterValues] = useState<TableFilterValues>({});
  const [isExporting, setIsExporting] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [viewItem, setViewItem] = useState<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const resolvedDataKey = dataKey || queryKey;

  const hasCreateAction = !!customAddAction || !!api.create;
  const hasUpdateAction = !!customEditAction || !!api.update;
  const hasDeleteAction = !!customDeleteAction || !!api.delete;
  const hasViewAction = enableView;

  const canCreate =
    showAddButton &&
    hasCreateAction &&
    (!resourceName || hasPermission(`${resourceName}:create`));
  const canView =
    hasViewAction && (!resourceName || hasPermission(`${resourceName}:view`));
  const canUpdate =
    enableEdit &&
    hasUpdateAction &&
    (!resourceName || hasPermission(`${resourceName}:update`));
  const canDelete =
    enableDelete &&
    hasDeleteAction &&
    (!resourceName || hasPermission(`${resourceName}:delete`));

  const { data, isLoading, isError } = useQuery(
    [
      queryKey,
      page,
      pageSize,
      sort?.field || "",
      sort?.direction || "",
      filterValues,
      ...queryDependencies,
    ],
    () =>
      api.getAll(page, pageSize, {
        sort,
        filters: filterValues,
      }),
    {
      keepPreviousData,
      enabled: queryEnabled,
    },
  );

  const currentItems = Array.isArray(data?.[resolvedDataKey])
    ? data[resolvedDataKey]
    : [];

  React.useEffect(() => {
    if (data && onDataLoaded) {
      onDataLoaded(data);
    }
  }, [data, onDataLoaded]);

  const createMutation = useMutation(api.create ?? (() => Promise.resolve()), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey]);
      onClose();
      toast({
        title: "آیتم با موفقیت ایجاد شد.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در ایجاد آیتم",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const updateMutation = useMutation(
    (formData: any) =>
      api.update ? api.update(selectedItem._id, formData) : Promise.resolve(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey]);
        onClose();
        toast({
          title: "آیتم با موفقیت ویرایش شد.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        toast({
          title: "خطا در ویرایش آیتم",
          description:
            error?.response?.data?.message || error?.message || "خطای ناشناخته",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      },
    },
  );

  const deleteMutation = useMutation(api.delete ?? (() => Promise.resolve()), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey]);
      toast({
        title: "آیتم حذف شد.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در حذف آیتم",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleCreate = () => {
    if (customAddAction) {
      customAddAction();
      return;
    }

    setSelectedItem(null);
    onOpen();
  };

  const handleEdit = (item: any) => {
    if (customEditAction) {
      customEditAction(item);
      return;
    }

    setSelectedItem(item);
    onOpen();
  };

  const handleView = (item: any) => {
    if (customViewAction) {
      void customViewAction(item);
      return;
    }

    setViewItem(item);
    onViewOpen();
  };

  const handleSubmit = async (formData: any) => {
    try {
      if (selectedItem) {
        await updateMutation.mutateAsync(formData);
      } else {
        await createMutation.mutateAsync(formData);
      }
    } catch (error) {
      // Error is handled by mutation's onError callback
    }
  };

  const handleDelete = (item: any) => {
    if (customDeleteAction) {
      customDeleteAction(item);
      return;
    }

    setItemToDelete(item);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteMutation.mutateAsync(itemToDelete._id);
        onDeleteClose();
        setItemToDelete(null);
      } catch (error) {
        onDeleteClose();
        setItemToDelete(null);
      }
    }
  };

  const sanitizeFileName = (value: string) =>
    value.trim().replace(/[\\/:*?"<>|]/g, "-");

  const sanitizeSheetName = (value: string) =>
    sanitizeFileName(value).slice(0, 31) || "Sheet1";

  const getNestedValue = (item: any, accessor: string) =>
    accessor
      .split(".")
      .reduce(
        (accumulator, key) =>
          accumulator === null || accumulator === undefined
            ? undefined
            : accumulator[key],
        item,
      );

  const normalizeCellValue = (
    value: any,
    accessor?: string,
  ): string | number | boolean => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    if (typeof value === "boolean") {
      return value ? "بله" : "خیر";
    }

    if (typeof value === "number" || typeof value === "string") {
      if (
        typeof value === "string" &&
        accessor &&
        (accessor.endsWith("At") || accessor.toLowerCase().includes("date"))
      ) {
        const parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleString("fa-IR");
        }
      }

      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map((entry) => normalizeCellValue(entry, accessor))
        .filter((entry) => entry !== "")
        .join(" | ");
    }

    if (value instanceof Date) {
      return value.toLocaleString("fa-IR");
    }

    if (typeof value === "object") {
      const preferredKeys = [
        "label",
        "title",
        "name",
        "fullName",
        "value",
        "nationalId",
        "_id",
      ];

      for (const key of preferredKeys) {
        if (
          value[key] !== undefined &&
          value[key] !== null &&
          value[key] !== ""
        ) {
          return normalizeCellValue(value[key], accessor);
        }
      }

      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }

    return String(value);
  };

  const fetchAllItems = async () => {
    const totalPages = Math.max(data?.totalPage || 1, 1);

    if (totalPages === 1) {
      return currentItems;
    }

    const responses = await Promise.all(
      Array.from({ length: totalPages }, (_, index) =>
        api.getAll(index + 1, pageSize, {
          sort,
          filters: filterValues,
        }),
      ),
    );

    return responses.flatMap((response) =>
      Array.isArray(response?.[resolvedDataKey])
        ? response[resolvedDataKey]
        : [],
    );
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const items = await fetchAllItems();

      if (!items.length) {
        toast({
          title: "داده‌ای برای خروجی اکسل وجود ندارد.",
          status: "info",
          duration: 2500,
          isClosable: true,
        });
        return;
      }

      const rows = items.map((item: any) =>
        columns.reduce<Record<string, string | number | boolean>>(
          (accumulator, column) => {
            if (column.excludeFromExport) {
              return accumulator;
            }

            const exportValue = column.exportValue
              ? column.exportValue(item)
              : getNestedValue(item, column.accessor);

            accumulator[column.header] = normalizeCellValue(
              exportValue,
              column.accessor,
            );
            return accumulator;
          },
          {},
        ),
      );

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        sanitizeSheetName(title),
      );
      XLSX.writeFile(workbook, `${sanitizeFileName(title)}.xlsx`);

      toast({
        title: "فایل اکسل با موفقیت دانلود شد.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "خطا در دانلود فایل اکسل",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <VStack w="100%" h="100%" spacing="0px" align="stretch">
        {beforeTable}
        <VStack w="100%" h="100%" justify="center" align="center">
          <Spinner size="xl" />
        </VStack>
        {afterTable}
      </VStack>
    );
  }

  if (isError) {
    return (
      <VStack w="100%" h="100%" spacing="0px" align="stretch">
        {beforeTable}
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {errorMessage}
        </Alert>
        {afterTable}
      </VStack>
    );
  }

  return (
    <VStack w="100%" h="100%" spacing="0px" align="stretch">
      {beforeTable && <Box mb={5}>{beforeTable}</Box>}
      <DataTable
        title={title}
        columns={columns}
        onAdd={canCreate ? handleCreate : undefined}
        onExport={handleExport}
        onView={canView ? handleView : undefined}
        onEdit={canUpdate ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        data={currentItems}
        totalData={data?.totalData || 0}
        currentPage={data?.currentPage || 1}
        pageSize={pageSize}
        totalPage={data?.totalPage || 1}
        onPageChange={setPage}
        showAddBtn={showAddButton}
        isExporting={isExporting}
        canAdd={canCreate}
        canExport={currentItems.length > 0}
        canView={canView}
        canEdit={canUpdate}
        canDelete={canDelete}
        sort={sort}
        filterDefinitions={filterDefinitions}
        filterValues={filterValues}
        onSort={(nextSort) => {
          setSort(nextSort);
          setPage(1);
        }}
        onFiltersChange={(nextFilters) => {
          setFilterValues(nextFilters);
          setPage(1);
        }}
      />
      {afterTable}

      {FormComponent && (canCreate || canUpdate) ? (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {selectedItem ? "ویرایش" : "ایجاد"} {title}
            </ModalHeader>
            <ModalCloseButton left={4} right="auto" aria-label="بستن" />
            <ModalBody py={4}>
              <FormComponent
                initialData={selectedItem}
                onSubmit={handleSubmit}
                onCancel={onClose}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      ) : null}

      {!customDeleteAction && canDelete ? (
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>تایید حذف</ModalHeader>
            <ModalCloseButton left={4} right="auto" aria-label="بستن" />
            <ModalBody py={4}>آیا از حذف این {title} مطمئن هستید؟</ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                لغو
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                isLoading={deleteMutation.isLoading}
              >
                حذف
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      ) : null}

      {!customViewAction && canView ? (
        <RecordDetailsModal
          isOpen={isViewOpen}
          onClose={() => {
            setViewItem(null);
            onViewClose();
          }}
          title={`جزئیات ${title}`}
          data={viewItem}
        />
      ) : null}
    </VStack>
  );
};
